import { ComparedItem, ComparedItemDetail, SelectedItem } from "./comparison_table";

// Cookie名
const COMAPARISON_TABLE_COOKIE_NAME = "comparedItemKeyList";
// 商品毎の区切り文字
const ITEM_SPLIT_CHARACTER = "&";
// localStorageのキー
const LOCAL_STORAGE_KEY = "COMPARED_ITEMS_DETAIL";
// バリエ内選択モーダルのページャーにおける、表示ページと非表示ページのマッピング
const hiddenPageMapping = {
  "1": ["2", "3"], // 1ページ目を表示する場合は、2ページ目と3ページ目を非表示にする
  "2": ["1", "3"], // 2ページ目を表示する場合は、1ページ目と3ページ目を非表示にする
  "3": ["1", "2"]  // 3ページ目を表示する場合は、1ページ目と2ページ目を非表示にする
};

/**
 * 比較表機能において、各画面に共通で必要な処理を担当するService。
 */
export class ComparisonTableService {

  /**
   * 比較表追加チェックボックスのchangeイベントに反応するイベントハンドラ
   * @param event イベントオブジェクト
   */
  static handleComparedItemChange(event: Event) {
    // changeイベントが発生したチェックボックス
    const checkBox = event.target as HTMLInputElement | null;
    if (!checkBox) return;

    // 対象商品の詳細情報をHTMLから集める。
    const comparedItem = this.collectItemDetailFromHtml(checkBox);
    if (!comparedItem) return;

    if (checkBox.checked) {
      // チェックを入れた場合は、Cookie等に対象商品を追加。
      this.addComparedItem(comparedItem);
    } else {
      // チェックを外した場合は、Cookie等から対象商品を削除。
      this.removeComparedItem(comparedItem.itemKey);
    }

    // ブラウザデフォルトの動作（チェックボックスのチェックON/OFFの描画）を有効化
    return true;
  }

  /**
   * CookieとlocalStorageに比較対象の商品情報を追加する。
   * @param comparedItem 比較対象の商品情報オブジェクト
   */
  private static addComparedItem(comparedItem: ComparedItem) {
    const itemKeyToAdd = comparedItem.itemKey;

    // Cookieを読み込む
    const comparedItemKeys = this.getItemKeysFromCookie();

    // Cookieに既に上限件数（10件）の商品が登録されている場合、追加しない。安全のため「10以上」で判定する。
    const alreadyFull = (comparedItemKeys.length >= 10);

    // 対象の商品キーがCookieに存在する場合は、追加しない。
    const alreadyAdded = comparedItemKeys.some(itemKey => itemKey === itemKeyToAdd);

    if (!alreadyFull && !alreadyAdded) {
      comparedItemKeys.push(itemKeyToAdd);
    }

    document.cookie = COMAPARISON_TABLE_COOKIE_NAME + "=" + encodeURIComponent(comparedItemKeys.join(ITEM_SPLIT_CHARACTER)) + "; path=/";

    // ローカルストレージに新たな商品を追加。
    const itemDetails = this.getlocalStorageValue();
    const newComparedItemDetail : ComparedItemDetail = {
      name: comparedItem.itemDetail.name,
      price: comparedItem.itemDetail.price,
      imageUrl: comparedItem.itemDetail.imageUrl,
      variationCode: comparedItem.itemDetail.variationCode
    };
    itemDetails[itemKeyToAdd] = newComparedItemDetail;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(itemDetails));
  }

  /**
   * Cookieとローカルストレージから比較対象の商品情報を削除する。
   * @param itemKeyToRemove 削除対象の商品キー（サプライヤID-カタログ商品コード）
   */
  static removeComparedItem(itemKeyToRemove: string) {
    // cookieを読み込み、商品キーたちを取り出す。
    const comparedItemKeys = this.getItemKeysFromCookie();
    
    // Cookieに設定されている商品キー情報が複数の場合、削除対象の商品キーだけをCookieから消す。
    const newValue = comparedItemKeys.filter(itemKey => itemKey !== itemKeyToRemove);
    document.cookie = COMAPARISON_TABLE_COOKIE_NAME + "=" + encodeURIComponent(newValue.join(ITEM_SPLIT_CHARACTER)) + "; path=/";

    // localStorageから削除
    this.removeFromlocalStorage(itemKeyToRemove);
  }

  /**
   * Cookieから比較対象の商品キーを取得する。
   * @returns 比較対象の商品キーリスト
   */
  static getItemKeysFromCookie(): string[] {
    const cookieWithEqual =  document.cookie.split('; ').find(eachCookie => eachCookie.startsWith(COMAPARISON_TABLE_COOKIE_NAME))
    if (!cookieWithEqual) {
      // Cookieが取得できない場合
      return [];
    }

    const cookieValue = cookieWithEqual.split('=')[1];
    if (!cookieValue) {
      return [];
    }

    return decodeURIComponent(cookieValue).split(ITEM_SPLIT_CHARACTER);
  }

  /**
   * ローカルストレージから比較対象の商品情報を削除する。
   * @param itemKeyToRemove 削除対象の商品キー（サプライヤID-カタログ商品コード）
   */
  private static removeFromlocalStorage(itemKey) {
    const itemDetails = this.getlocalStorageValue();
    delete itemDetails[itemKey];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(itemDetails));
  }

  /**
   * ローカルストレージから比較対象の商品情報を取得する（生データをオブジェクトに変換して返却する）。
   * @returns 比較対象の商品情報リスト
   */
  static getComparedItemsDetail(): ComparedItem[] {

    const itemDetails = this.getlocalStorageValue();
    if (!itemDetails) {
      return [];
    }

    // cookieを読み込み、商品キーたちを取り出す。
    const comparedItemKeys = this.getItemKeysFromCookie();

    // ローカルストレージから商品の詳細情報を取り出す。
    const comparedItemsDetailNullable : (ComparedItem | null)[] = comparedItemKeys.map(itemKey => {
        const itemDetail = itemDetails[itemKey] as ComparedItemDetail;

        if (!itemDetail) {
          // ローカルストレージに対象商品の情報がない場合、後から除外できるようにnullを返却。
          return null;
        }

        const comparedItem : ComparedItem = {
          itemKey,
          itemDetail
        }

        return comparedItem;
    })
    
    // nullを除外する。
    return comparedItemsDetailNullable.filter(itemDetail => itemDetail !== null) as ComparedItem[]
  }

  /**
   * ローカルストレージから比較対象の商品情報の生データを取得する。
   * @returns 比較対象の商品情報リスト
   */
  private static getlocalStorageValue() : {string?: ComparedItemDetail} {
      // ローカルストレージから比較対象商品たちの詳細を取得。
      const localStorageValue= localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!localStorageValue) {
        // ローカルストレージにそもそもキーが存在しない場合。
        return {};
      }
  
      // ローカルストレージ内ではJSON文字列で格納されている。
      // そこで、JSON文字列からオブジェクトに変換する。
      return JSON.parse(localStorageValue);
  }

  /**
   * HTMLから比較対象の商品情報を集める。
   * @param checkBox 「比較表に追加」のチェックボックス
   * @returns 比較対象の商品情報オブジェクト（1件）
   */
  private static collectItemDetailFromHtml(checkBox: HTMLElement): ComparedItem | null {

    const itemKey = checkBox.getAttribute("data-product-code"); // {サプライヤID}-{カタログ商品コード}　の形式
    if (!itemKey) {
      return null;
    }

    // 対象の単品が属するバリエーションのバリエーションコード。
    // データ属性名に「hapy」が付く理由については、「variation_select_area_modal_each_page.html」内のコメントを参照。
    const variationCodeInModal = checkBox.getAttribute("data-variation-code-hapy");

    // バリエーション選択エリアモーダルでのみ設定される、モーダルを特定するID
    const modalId = checkBox.getAttribute("data-modal-id");
    
    // CXのJSを呼び出して、HTMLから商品情報を集める。
    const selectedItem: SelectedItem = (window as any).BottomTrackingAddComparison.getProductInfo(checkBox, modalId);

    const comparedItem : ComparedItem = {
      itemKey,
      itemDetail : {
        name : selectedItem.text,
        price : selectedItem.priceStr ? selectedItem.priceStr : "",
        imageUrl: selectedItem.imgSrc,
        variationCode : selectedItem.variationCode ? selectedItem.variationCode : variationCodeInModal
      }
    }

    return comparedItem;
  }

  /**
   * ローカルストレージをクリーニングする。
   * ローカルストレージに存在するが、Cookieには存在しない商品を削除する。
   */
  static clearLocalStorage() {
    const cookieItemKeys = this.getItemKeysFromCookie();
    const localStorageItemKeys = Object.keys(this.getlocalStorageValue());

    // ローカルストレージに存在するが、Cookieには存在しない商品を削除。
    const itemKeysToRemove = localStorageItemKeys.filter(localStorageItemKey => !this.hasTarget(localStorageItemKey, cookieItemKeys));
    itemKeysToRemove.forEach(itemKey => this.removeFromlocalStorage(itemKey));
  }

  /**
   * 引数「srcArray」に引数「target」が存在するか否か判定する。
   * @returns 存在すればtrue
   */
  private static hasTarget(target: string, srcArray: string[]): boolean {
    return srcArray.some(srcElement => srcElement === target);
  }

  /**
   * CX側の初期化処理を呼び出す。
   * キーワード検索結果画面などで絞り込みが行われた場合に呼び出される。
   */
  static initDesignJS() {

    // 単品用の「比較表に追加」チェックボックスを初期化する。
    (window as any).BottomTrackingAddComparison.setInitCheckbox();

    // バリエーション選択エリアモーダルを初期化する。
    const variationModals = document.querySelectorAll('.js-bottomTrackingAddComparison_modalWrap')
    if (!variationModals) {
      return;
    }
    variationModals.forEach(modal => {
      (window as any).BottomTrackingAddComparison.setInitCheckbox(modal.id);
    });
  }

  /**
   * 各商品の×ボタン(削除ボタン)のclickイベントに反応するイベントハンドラ
   * @param event イベントオブジェクト
   */
  static async handleDeleteItemButtonClick(event: Event) {
    const button = event.currentTarget as HTMLButtonElement | null;
    if (!button) return;

    // 商品キーを取得
    const itemKey = button.getAttribute('data-product-code');
    if (!itemKey) return;

    // Cookie、localStorageから対象商品を削除
    this.removeComparedItem(itemKey);
  }

  /**
   * 全商品を削除するボタンのclickイベントに反応するイベントハンドラ
   */
  static handleDeleteAllItemsButtonClick() {
    this.getItemKeysFromCookie().forEach(itemKey => {
      this.removeComparedItem(itemKey);
    })
  }

  /**
   * バリエ内選択モーダル内のページャーボタンのclickイベントに反応するイベントハンドラ
   * @param event イベントオブジェクト
   */
  static handlePagerButtonClick(event: Event) {

    const pageButton = event.currentTarget as HTMLAnchorElement;

    const targetPageNo = pageButton.dataset.pageNo;
    const variationCode = pageButton.dataset.variationCode;

    if(!targetPageNo || !variationCode) {
      return;
    }

    const hiddenPageNoList = hiddenPageMapping[targetPageNo];

    // 表示対象以外のページを【非表示】にする。
    hiddenPageNoList.forEach(hiddenPageNo => {
      const itemsPartsToHide = document.getElementById(`variation-select-area-modal-items-${variationCode}-${hiddenPageNo}`);
      if (itemsPartsToHide) {
        itemsPartsToHide.style.display = "none";
      }
      const pargerPartsToHide = document.getElementById(`variation-select-area-modal-pager-${variationCode}-${hiddenPageNo}`);
      if (pargerPartsToHide) {
        pargerPartsToHide.style.display = "none";
      }
    });

    // 表示対象以外のページを【表示】する。
    const itemsPartsToShow = document.getElementById(`variation-select-area-modal-items-${variationCode}-${targetPageNo}`);
    if (itemsPartsToShow) {
      itemsPartsToShow.style.display = "";
    }
    const pagerPartsToShow = document.getElementById(`variation-select-area-modal-pager-${variationCode}-${targetPageNo}`);
    if (pagerPartsToShow) {
      pagerPartsToShow.style.display = "";
    }
  }
}
